/**
 * AD-015's kill camera, as rules rather than as a renderer: when the corner
 * window opens, whom it watches, when it shuts, and what rectangle of the view
 * it occupies.
 *
 * Pure — no `three`, no DOM — because all four of those are decisions rather
 * than drawing, and because the interesting failure modes (a second kill while
 * the first is still falling, a victim leaving the roster, the toggle going off
 * mid-fall) are cheap to state and expensive to catch by eye. `scene-killcam.ts`
 * is the half that owns a camera and a render pass.
 *
 * ## Whose death opens it
 *
 * **Victims of the player's side**, not strictly the player's own kills. The
 * original's spirit is "your victim" — `collide.c`'s announcement block says
 * *You just shot down…* — but the original also had no picture-in-picture to
 * hang the distinction on, so there is nothing to be faithful *to* here. Two
 * things settle it for a wingman's kill:
 *
 * - The engine does not hand the renderer an attacker at the fatal blow in a
 *   form a view can read. `CombatEvent.attackerId` exists, but events live for
 *   one tick and a frame can run several (`sim-runner.ts` harvests them per
 *   tick); a *status transition* is a level the view carries every frame and
 *   cannot be missed by a long frame. Keying off the transition is what makes
 *   this module a pure function of the roster.
 * - A four-ship escort in which only the player's own kills are ever shown
 *   would leave the window dark through most of a big fight, which is the
 *   opposite of what a kill cam is for.
 *
 * So: an aeroplane on the *other* side (`AircraftView.friendly === false`)
 * going fatally out of control opens the window, whoever hit it. An enemy that
 * goes straight from `flying` to `destroyed` — AD-014's "flown into the ground
 * from level flight", provably never a fall — is not followed: by the time the
 * renderer sees it the wreck is already down, and this camera exists to watch
 * something come down.
 */

import type { AircraftStatus } from "@red-opus/engine";

/** The three fields the rules read off an `AircraftView`. Structural, so a test can hand-build a roster. */
export interface KillCamAircraft {
  readonly id: string;
  readonly status: AircraftStatus;
  /** Same side as the seat being flown from (`encounter-views.ts`). Only the enemy's dying is worth watching. */
  readonly friendly: boolean;
}

/**
 * How long the window stays up after the wreck arrives. Long enough for the
 * explosion sprite sequence to play out and register as an impact, short enough
 * that it never feels like a cutscene the player is being held in.
 * CALIBRATION-PENDING, like every constant in AD-014's fall.
 */
export const KILL_CAM_HOLD_SECONDS = 1.5;

export interface KillCamLayout {
  /** Fraction of the view's width the window occupies. */
  readonly widthFraction: number;
  /** Width divided by height — 4:3, the original's own screen shape. */
  readonly aspect: number;
  /** Inset from the top and right edges, in CSS pixels. Matches `.killcam-frame`'s `1rem`. */
  readonly marginPx: number;
  /** Below this the window would show nothing readable, so it is not drawn at all. */
  readonly minWidthPx: number;
}

/**
 * 28 % of the width at 4:3 is roughly the size of the original's own VCR
 * inset, and leaves the centre of the screen — where the fight the player is
 * still flying happens — completely clear.
 */
export const KILL_CAM_LAYOUT: KillCamLayout = {
  widthFraction: 0.28,
  aspect: 4 / 3,
  marginPx: 16,
  minWidthPx: 160,
};

/**
 * The window's rectangle in the renderer's own units: **CSS pixels, measured
 * from the top-left**, which is what `Renderer.setViewport`/`setScissor`
 * document ("the upper left corner of the viewport origin") and what the
 * WebGPU backend passes through to `setScissorRect` unchanged. The same numbers
 * are therefore also CSS `top`/`right` offsets, which is why the DOM frame in
 * `app.css` can be written as plain percentages and stay aligned.
 */
export interface KillCamRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the window goes in a view of this size, or `null` when the view is too
 * small to hold one — a narrow window is better off with no inset at all than
 * with a postage stamp obscuring a quarter of it.
 */
export function killCamRect(
  viewWidthCss: number,
  viewHeightCss: number,
  layout: KillCamLayout = KILL_CAM_LAYOUT,
): KillCamRect | null {
  const width = Math.round(Math.max(viewWidthCss * layout.widthFraction, layout.minWidthPx));
  const height = Math.round(width / layout.aspect);
  const x = Math.round(viewWidthCss - layout.marginPx - width);
  if (x < layout.marginPx) return null;
  // Half the view's height is the ceiling: past that the "corner window" is a
  // split screen, and the main view is what the player is flying on.
  if (height + layout.marginPx > viewHeightCss / 2) return null;
  return { x, y: layout.marginPx, width, height };
}

export interface KillCamState {
  /** Whose fall is being watched; `null` while the window is shut. */
  readonly victimId: string | null;
  /** Seconds since the wreck reached the ground. Zero while it is still falling. */
  readonly sinceImpactSeconds: number;
  /**
   * Each aeroplane's status as of the previous frame, so an *onset* is a
   * transition rather than a level. Without it the window would reopen on
   * every frame of a fall, and would open on frame one for anything that
   * happened to spawn already out of control.
   */
  readonly seen: ReadonlyMap<string, AircraftStatus>;
}

/** Nothing watched, nothing seen — what a scene starts on and what every close returns to. */
export const CLOSED_KILL_CAM: KillCamState = { victimId: null, sinceImpactSeconds: 0, seen: new Map() };

export interface KillCamInput {
  /** The toggle, and whether this route offers the feature at all (`/duel` does not). */
  readonly enabled: boolean;
  /** Everyone but the seat being flown from — `EncounterRunner.others()`. */
  readonly aircraft: readonly KillCamAircraft[];
  readonly dt: number;
  /**
   * True once the flight has been decided. A decided flight stops the window
   * *opening*, but a window already holding on a wreck plays its beat out: the
   * last kill of a fight is precisely the one worth watching land, and it
   * lands on the same tick the fight is judged (AD-014 §3a). A victim still
   * falling when the verdict arrives is dropped instead — the runner has
   * frozen, so it would hang in the air for as long as the player left the
   * debrief up.
   */
  readonly ended: boolean;
}

/** True while the corner window should be drawn. */
export function killCamIsOpen(state: KillCamState): boolean {
  return state.victimId !== null;
}

/**
 * The newest enemy to go fatally out of control *this frame*, or `null`.
 *
 * "Newest" is roster order rather than a timestamp: several aeroplanes can
 * cross into `outOfControl` on one frame (a frame that ran several ticks, or a
 * flak burst that caught two), and the window shows one at a time. An aeroplane
 * with no previous status is being seen for the first time and is only
 * recorded, never triggered on — otherwise joining a fight already in progress
 * would open the window on somebody else's long-finished kill.
 */
function fatalOnsetIn(state: KillCamState, aircraft: readonly KillCamAircraft[]): string | null {
  let victim: string | null = null;
  for (const entry of aircraft) {
    if (entry.friendly || entry.status !== "outOfControl") continue;
    const before = state.seen.get(entry.id);
    if (before === undefined || before === "outOfControl" || before === "destroyed") continue;
    victim = entry.id;
  }
  return victim;
}

function statusesOf(aircraft: readonly KillCamAircraft[]): ReadonlyMap<string, AircraftStatus> {
  const seen = new Map<string, AircraftStatus>();
  for (const entry of aircraft) seen.set(entry.id, entry.status);
  return seen;
}

/**
 * One frame of the window's life.
 *
 * The order of the four rules is the whole behaviour: a fresh kill wins over
 * whatever is being watched (a second victim while the first is still falling
 * re-targets to the newer one, which is what a player who has just fired
 * expects to see), a victim that has left the roster closes the window at once,
 * a wreck on the ground starts the beat, and everything else is "keep watching".
 */
export function nextKillCamState(state: KillCamState, input: KillCamInput): KillCamState {
  const seen = statusesOf(input.aircraft);
  const closed: KillCamState = { victimId: null, sinceImpactSeconds: 0, seen };
  if (!input.enabled) return closed;

  const onset = input.ended ? null : fatalOnsetIn(state, input.aircraft);
  if (onset !== null) return { victimId: onset, sinceImpactSeconds: 0, seen };
  if (state.victimId === null) return closed;

  const victim = input.aircraft.find((entry) => entry.id === state.victimId);
  if (!victim) return closed;
  if (victim.status !== "destroyed") return input.ended ? closed : { ...state, sinceImpactSeconds: 0, seen };

  const held = state.sinceImpactSeconds + Math.max(input.dt, 0);
  if (held >= KILL_CAM_HOLD_SECONDS) return closed;
  return { victimId: state.victimId, sinceImpactSeconds: held, seen };
}
